import { useEffect } from 'react';
import { BrowserRouter, Routes, Route, Navigate, useNavigate } from 'react-router-dom';
import { useAtom, useSetAtom, SetStateAction } from 'jotai';
import { ThemeProvider, createTheme } from '@mui/material/styles';
import CssBaseline from '@mui/material/CssBaseline';
import Box from '@mui/material/Box';
import { IAdmin } from 'common';

import { adminAtom, appLoadedAtom, authLoadingAtom, isAuthenticatedAtom } from './store/atoms';
import { loadAdminInfo, setAppLoaded } from './store/actions';

import Crawlings from './containers/Crawlings';
import Login from './containers/Login';
import AppLoading from './containers/AppLoading';
import NavBar from './components/NavBar';
import NotFound from './containers/NotFound';
import Users from './containers/Users';
import Groups from './containers/Groups';
import { Administrators } from './containers/Administrators';

import { style } from 'typestyle';
import { ADALONG_COLORS } from './styles';

// Create MUI v5 theme with dark colors matching web-front
const theme = createTheme({
  palette: {
    mode: 'dark',
    primary: {
      main: ADALONG_COLORS.FLASH_BLUE,
    },
    secondary: {
      main: ADALONG_COLORS.FLASH_RED,
    },
    background: {
      default: ADALONG_COLORS.SMOOTH_BLACK,
      paper: ADALONG_COLORS.LIGHT_BLACK,
    },
  },
  typography: {
    fontFamily: '"Public Sans", sans-serif',
  },
});

function AppContent() {
  const [isAuthenticated] = useAtom(isAuthenticatedAtom);
  const [appLoaded] = useAtom(appLoadedAtom);
  const setAdmin = useSetAtom(adminAtom);
  const setLoaded = useSetAtom(appLoadedAtom);
  const setAuthLoading = useSetAtom(authLoadingAtom);
  const setIsAuthenticated = useSetAtom(isAuthenticatedAtom);
  const navigate = useNavigate();

  useEffect(() => {
    // Load admin info on mount
    const initializeApp = async () => {
      try {
        const loggedIn = await loadAdminInfo((atom, value) => {
          if (Object.is(atom, adminAtom)) setAdmin(value as SetStateAction<IAdmin | null>);
          if (Object.is(atom, isAuthenticatedAtom)) setIsAuthenticated(value as SetStateAction<boolean>);
          if (Object.is(atom, authLoadingAtom)) setAuthLoading(value as SetStateAction<boolean>);
        });

        if (loggedIn) {
          if (window.location.pathname === '/login' || window.location.pathname === '/') {
            navigate('/users');
          }
        } else if (window.location.pathname !== '/login') {
          navigate('/login');
        }
      } catch (error) {
        console.error('Failed to initialize app:', error);
        if (window.location.pathname !== '/login') {
          navigate('/login');
        }
      } finally {
        setAppLoaded((_atom, value) => {
          setLoaded(value as SetStateAction<boolean>);
        });
      }
    };

    initializeApp();
  }, []);

  const handleScroll = (e: React.UIEvent<HTMLDivElement>) => {
    const { currentTarget } = e;
    const scrollBottom = currentTarget.scrollHeight - currentTarget.scrollTop - currentTarget.clientHeight;
    window.dispatchEvent(new CustomEvent('scrollbottom', { detail: scrollBottom }));
  };

  if (!appLoaded) {
    return <AppLoading />;
  }

  if (!isAuthenticated) {
    return (
      <>
        <AppLoading />
        <Routes>
          <Route path="/login" element={<Login />} />
          <Route path="*" element={<Navigate to="/login" replace />} />
        </Routes>
      </>
    );
  }

  return (
    <Box className={app}>
      <AppLoading />
      <Box className={navBar}>
        <NavBar />
      </Box>
      <Box className={content} onScroll={handleScroll}>
        <Box className={container}>
          <Routes>
            <Route path="/" element={<Navigate to="/users" replace />} />
            <Route path="/login" element={<Login />} />
            <Route path="/users" element={<Users />} />
            <Route path="/administrators" element={<Administrators />} />
            <Route path="/groups" element={<Groups />} />
            <Route path="/crawling" element={<Crawlings />} />
            <Route path="*" element={<NotFound />} />
          </Routes>
        </Box>
      </Box>
    </Box>
  );
}

function App() {
  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <BrowserRouter>
        <AppContent />
      </BrowserRouter>
    </ThemeProvider>
  );
}

const app = style({
  display: 'flex',
  flexDirection: 'column',
  height: '100vh',
  width: '100vw',
  background: ADALONG_COLORS.SMOOTH_BLACK,
  position: 'relative',
});

const navBar = style({
  width: '100%',
  flexShrink: 0,
  zIndex: 10,
});

const content = style({
  flex: 1,
  overflow: 'auto',
  display: 'flex',
  flexDirection: 'column',
  position: 'relative',
});

const container = style({
  flex: 1,
  padding: '32px 24px',
  minHeight: 0,
  display: 'flex',
  flexDirection: 'column',
});

export default App;
